import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  CreditCard,
  Landmark,
  ShieldCheck,
  CheckCircle2,
  Info,
} from "lucide-react";

import PaymentSummary from "./PaymentSummary";

export default function PaymentInfo({ userEmail }) {
  const [payoutMethod, setPayoutMethod] = useState("etransfer");
  const [payoutEmail, setPayoutEmail] = useState("");
  const [legalName, setLegalName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  const navigate = useNavigate();

  const isValidEmail = /\S+@\S+\.\S+/.test(payoutEmail);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    const loadPaymentInfo = async () => {
      try {
        const res = await fetch(
          `/api/paymentInfo?email=${encodeURIComponent(userEmail)}`
        );
        const data = await res.json();

        if (res.ok && data.success && data.paymentInfo) {
          setPayoutMethod(data.paymentInfo.payoutMethod || "etransfer");
          setPayoutEmail(data.paymentInfo.payoutEmail || "");
          setLegalName(data.paymentInfo.legalName || "");
          setNotes(data.paymentInfo.notes || "");
        }
      } catch (err) {
        console.error("Error loading payment info:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPaymentInfo();
  }, [userEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!legalName.trim() || !payoutEmail.trim() || !isValidEmail) {
      alert("Please complete all required fields with a valid email.");
      return;
    }

    try {
      setSaving(true);

      const res = await fetch("/api/savePaymentInfo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userEmail,
          legalName,
          payoutMethod,
          payoutEmail,
          notes,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert("Payment info saved successfully.");
        navigate("/");
      } else {
        alert(data.message || "Failed to save payment info.");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving payment info.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="mt-4 max-w-2xl">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Payment Info
            </h1>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Add the payout details you want used when event funds are sent to
              you. For now, payouts can be sent by E-Transfer or PayPal.
            </p>
          </div>
        </header>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50/80 px-6 py-5 sm:px-8">
              <h2 className="text-lg font-semibold text-slate-900">
                Payout details
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Choose how you’d like to receive organizer payouts.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-6 px-6 py-6 sm:px-8"
            >
              <div>
                <label className="mb-3 block text-sm font-medium text-slate-700">
                  Payout Method
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setPayoutMethod("etransfer")}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      payoutMethod === "etransfer"
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Landmark className="h-5 w-5" />
                      <div>
                        <p className="font-semibold">E-Transfer</p>
                        <p
                          className={`mt-1 text-sm ${
                            payoutMethod === "etransfer"
                              ? "text-white/75"
                              : "text-slate-500"
                          }`}
                        >
                          Send payout to an email address
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPayoutMethod("paypal")}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      payoutMethod === "paypal"
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5" />
                      <div>
                        <p className="font-semibold">PayPal</p>
                        <p
                          className={`mt-1 text-sm ${
                            payoutMethod === "paypal"
                              ? "text-white/75"
                              : "text-slate-500"
                          }`}
                        >
                          Send payout to a PayPal email
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Legal Name / Payee Name
                </label>
                <input
                  type="text"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Enter full name or business name"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  {payoutMethod === "etransfer"
                    ? "E-Transfer Email"
                    : "PayPal Email"}
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={payoutEmail}
                    onChange={(e) => setPayoutEmail(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder={
                      payoutMethod === "etransfer"
                        ? "name@example.com"
                        : "paypal@example.com"
                    }
                    required
                  />
                </div>

                {payoutEmail && !isValidEmail && (
                  <p className="mt-2 text-sm text-red-600">
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Notes <span className="text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Add any payout instructions or organizer notes"
                />
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row">
                <button
                  type="submit"
                  disabled={saving || loading}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white transition sm:w-auto ${
                    saving || loading
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Payment Info"}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200 sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              </div>
            </form>
          </section>

          <aside className="space-y-6">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50/80 px-6 py-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      How payouts work
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Simple payout options for organizers while Stripe is not yet connected.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowInfo((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  >
                    <Info className="h-4 w-4" />
                    {showInfo ? "Hide Info" : "Info"}
                  </button>
                </div>
              </div>

              <div className="space-y-4 px-6 py-6">
                {showInfo && (
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                    <p className="text-sm font-medium text-slate-900">
                      Bi-weekly payout schedule
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Organizer payouts are processed every two weeks. Any ticket
                      sales collected during the current payout window will be
                      sent to your selected payout method on the next payout run.
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Funds will be sent to the email entered on this page using
                      your selected method: E-Transfer or PayPal.
                    </p>
                  </div>
                )}

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    E-Transfer
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Best for smaller local promoters who want a straightforward
                    email-based payout.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">PayPal</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Useful for organizers already receiving business payments
                    through PayPal.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    Stripe later
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    This page can later expand into Stripe Connect without
                    changing the overall organizer flow.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-sm">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-white/80" />
                <p className="text-sm font-medium text-white/75">
                  Organizer payout note
                </p>
              </div>

              <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                Keep your payout email accurate
              </h3>
              <p className="mt-3 text-sm leading-6 text-white/75">
                Funds will be sent to the email entered here, so organizers
                should double-check it before saving.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}